// hooks/useUnreadCounts.ts
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';

import { db } from '../config';
import { isRentalAwaitingConfirmation } from '../utils/rentalAwaitingConfirmation';

/**
 * Badge w dzwonku TopBar liczy WSZYSTKIE nieprzeczytane powiadomienia
 * dla danej organizacji (bez okna czasowego). Wcześniej był 24h filter,
 * ale wymagał composite indexa (hotelId + isRead + createdAt range) który
 * nie istnieje w Firestore — query rzucało po cichu i badge zawsze pokazywał
 * 0. Bez filtra: prosta dwukluczowa query, działa bez composite index.
 *
 * Świeżość komunikuje TTL: backend co 90 dni TTL'uje archiwalne dokumenty,
 * a użytkownik może ręcznie „Oznacz wszystkie" w popoverze.
 */

interface UnreadCounts {
    unreadEmails: number;
    unreadTasks: number;
    unreadNotifications: number;
    unreadChatCount: number;
    newReportsCount: number;
    newOrdersCount: number;
    newShoppingOrdersCount: number;
    newReservationsCount: number;
}

/* Dźwięku TU JUŻ NIE MA. Alerty (dzwonek + powiadomienie systemowe) obsługuje
   `useNotificationAlerts` na tej samej kolekcji — ten hook liczy wyłącznie badge.
   Wcześniej oba grały z osobnymi licznikami debounce'u, więc jedno zamówienie
   potrafiło zadzwonić dwa razy. */

export const useUnreadCounts = (userId: string | undefined, organizationId?: string | null) => {
  const [counts, setCounts] = useState<UnreadCounts>({
    unreadEmails: 0,
    unreadTasks: 0,
    unreadNotifications: 0,
    unreadChatCount: 0,
    newReportsCount: 0,
    newOrdersCount: 0,
    newShoppingOrdersCount: 0,
    newReservationsCount: 0,
  });

  const previousCountsRef = useRef<UnreadCounts>(counts);
  const isFirstLoadRef = useRef(true);

  // Initialize global audio on first hook mount
  useEffect(() => {
  }, []);

  useEffect(() => {
    if (!userId || !organizationId) {
      return;
    }

    // Reset counts immediately when organizationId changes
    const resetCounts: UnreadCounts = {
      unreadEmails: 0,
      unreadTasks: 0,
      unreadNotifications: 0,
      unreadChatCount: 0,
      newReportsCount: 0,
      newOrdersCount: 0,
      newShoppingOrdersCount: 0,
      newReservationsCount: 0,
    };
    setCounts(resetCounts);
    previousCountsRef.current = resetCounts;
    isFirstLoadRef.current = true;

    // ==================== EMAILS LISTENER ====================
    // „Nieprzeczytane" = `readBy` nie zawiera użytkownika. Firestore nie ma operatora
    // „array-not-contains", więc ten jeden warunek sprawdzamy w przeglądarce. Organizację
    // filtruje już serwer (wcześniej listener pobierał maile użytkownika ze WSZYSTKICH
    // organizacji, także przeczytane, i odrzucał je dopiero po stronie klienta).
    // Wymaga indeksu złożonego `inmails`: sendTo (array-contains) + organizationId.
    const emailsQuery = query(
      collection(db, 'inmails'),
      where('sendTo', 'array-contains', userId),
      where('organizationId', '==', organizationId),
    );

    const unsubscribeEmails = onSnapshot(emailsQuery, (snapshot) => {
      const unread = snapshot.docs.filter((doc) => {
        const readBy = doc.data().readBy || [];
        return !readBy.includes(userId);
      }).length;

      setTimeout(() => {
        setCounts((prev) => {
          const newCounts = { ...prev, unreadEmails: unread };

          previousCountsRef.current = newCounts;
          return newCounts;
        });
      }, 0);
    });

    // ==================== TASKS LISTENER ====================
    // Use onSnapshot with snapshot.size for realtime updates
    // Firebase optimizes this internally - only metadata is transferred, not full documents
    const tasksQuery = query(
      collection(db, 'tasks'),
      where('assignedTo', '==', userId),
      where('completed', '==', false),
    );

    const unsubscribeTasks = onSnapshot(
      tasksQuery,
      (snapshot) => {
        setTimeout(() => {
          setCounts((prev) => {
            const newCounts = { ...prev, unreadTasks: snapshot.size };

            previousCountsRef.current = newCounts;
            return newCounts;
          });
        }, 0);
      },
      () => {},
    );

    // ==================== NOTIFICATIONS LISTENER ====================
    // Prosta query — `isRead + hotelId`. NIE dodawać tu range filtra
    // (np. createdAt >= X) bez utworzenia composite indexa w Firestore,
    // bo query zacznie po cichu zwracać 0 (dawny bug: badge zawsze pusty).
    const notificationsQuery = query(
      collection(db, 'users', userId, 'notifications'),
      where('isRead', '==', false),
      where('hotelId', '==', organizationId),
    );

    const unsubscribeNotifications = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        setCounts((prev) => {
          const newCounts = { ...prev, unreadNotifications: snapshot.size };
          previousCountsRef.current = newCounts;
          return newCounts;
        });
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('[useUnreadCounts] notifications listener error', error);
      },
    );

    // ==================== REPORTS/TASKS LISTENER ====================
    // Listening for new reports (tasks with status 'new')
    const reportsQuery = query(
      collection(db, `hotels/${organizationId}/tasks`),
      where('status', '==', 'new'),
      where('isArchived', '==', false),
    );

    const unsubscribeReports = onSnapshot(
      reportsQuery,
      (snapshot) => {
        setTimeout(() => {
          setCounts((prev) => {
            const newCounts = { ...prev, newReportsCount: snapshot.size };

            previousCountsRef.current = newCounts;
            return newCounts;
          });
        }, 0);
      },
      () => {},
    );

    // ==================== ORDERS LISTENER (Room Service) ====================
    const ordersQuery = query(
      collection(db, `hotels/${organizationId}/orders`),
      where('status', '==', 'new'),
    );

    const unsubscribeOrders = onSnapshot(
      ordersQuery,
      (snapshot) => {
        setTimeout(() => {
          setCounts((prev) => {
            const newCounts = { ...prev, newOrdersCount: snapshot.size };

            previousCountsRef.current = newCounts;
            return newCounts;
          });
        }, 0);
      },
      () => {},
    );

    // ==================== PRODUCT ORDERS LISTENER (Shopping) ====================
    const productOrdersQuery = query(
      collection(db, `hotels/${organizationId}/product-orders`),
      where('status', '==', 'new'),
    );

    const unsubscribeProductOrders = onSnapshot(
      productOrdersQuery,
      (snapshot) => {
        setTimeout(() => {
          setCounts((prev) => {
            const newCounts = { ...prev, newShoppingOrdersCount: snapshot.size };

            previousCountsRef.current = newCounts;
            return newCounts;
          });
        }, 0);
      },
      () => {},
    );

    // ==================== RESERVATIONS LISTENER ====================
    const reservationsQuery = query(
      collection(db, `hotels/${organizationId}/productRentalsReservations`),
      // Od #157 rezerwacja gościa czeka na potwierdzenie w statusie `new`.
      where('status', '==', 'new'),
    );

    const unsubscribeReservations = onSnapshot(
      reservationsQuery,
      (snapshot) => {
        setTimeout(() => {
          setCounts((prev) => {
            const newReservationsCount = snapshot.docs.filter((d) => isRentalAwaitingConfirmation(d.data())).length;
            const newCounts = { ...prev, newReservationsCount };

            previousCountsRef.current = newCounts;
            return newCounts;
          });
        }, 0);
      },
      () => {},
    );

    // Po pierwszym załadowaniu ustaw flagę na false
    const timer = setTimeout(() => {
      isFirstLoadRef.current = false;
    }, 2000); // ✅ Zwiększ do 2 sekund dla pewności

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timer);
      unsubscribeEmails();
      unsubscribeTasks();
      unsubscribeNotifications();
      unsubscribeReports();
      unsubscribeOrders();
      unsubscribeProductOrders();
      unsubscribeReservations();
    };
  }, [userId, organizationId]);

  return counts;
};
